import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrandVerifiedIcon,
  CloseIcon,
  SquareWarnIcon,
  NoMediaLinkIcon,
  UploadIcon2,
  TriangleExclamationIco2,
  AiReviewNoDataIcon
} from '@/components/common/icon';
import { Button, Loading } from 'aha-components';
import ModalUpdataAudience from '../ModalUpdataAudience';
import { PlatformItems } from '@/interface/common';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { openPhylloPlatformConnect } from '@/biz/openPhylloPlatformConnect';
import { getOrderId, isMobile } from '@/biz/tool';
import { showSocialBindingBrowserFallbackIfNeeded } from '@/app/socialBinding/browserFallback';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { AudienceViewSource, SelfEstimatedStatus, NoneSource } from './config';
import WebModal from '../Modal';

const countryCodeToFlag = (code: string) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const DONUT_COLORS = ['#7570B3', '#A6CEE3', '#E6AB02', '#1B9E77'];

const GENDERS_COLORS_ = ['#60A5FA', '#C084FC'];

const DonutChart = ({
  data,
  size = 80,
  boxSize = 100
}: {
  data: { name: string; value: number; color: string }[];
  size?: number;
  boxSize?: number;
}) => {
  const r = (size - 8) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const segmentGap = data.length > 1 ? 2 : 0;
  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`-10 -10 ${boxSize} ${boxSize}`}>
      {data.map((item, i) => {
        const pct = item.value / 100;
        const dashLen = Math.max(0, circumference * pct - segmentGap);
        const dashOffset =
          circumference * (1 - accumulated) + circumference * 0.25;
        accumulated += pct;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={item.color}
            strokeWidth={size * 0.18}
            strokeDasharray={`${dashLen} ${circumference - dashLen}`}
            strokeDashoffset={dashOffset}
            style={{
              transition: 'stroke-dasharray 0.4s, stroke-dashoffset 0.4s'
            }}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={r - size * 0.13} fill="white" />
    </svg>
  );
};

interface Props {
  audienceError?: boolean;
  setAudienceError?: (v: boolean) => void;
  setHasAudienceData?: (v: boolean) => void;
}

const AudienceDemographics: React.FC<Props> = ({
  audienceError = false,
  setAudienceError = () => {},
  setHasAudienceData = () => {}
}) => {
  const { t } = useTranslation();
  const { influencers, fetchPlatformData } = usePlatformStore();
  const [showModalUpdataAudience, setShowModalUpdataAudience] = useState(false);
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { showModalTakeOrder, setThisLoading, setTakeOrderAudienceView } =
    useWebModalStore();
  const orderData = detailData?.order;
  const orderInfluencerData = detailData?.influencer;
  const [showImg, setShowImg] = useState(false);
  // 受众数据轮询
  const audienceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waitingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [audienceData, setAudienceData] = useState<any>(null);
  const [waitingTime, setWaitingTime] = useState(0);
  const [isInsOrder, setIsInsOrder] = useState(false);
  const [linkChannelLoading, setLinkChannelLoading] = useState(false);
  const [isInfluencerBinding, setIsInfluencerBinding] = useState(false); // 当前订单对应的达人是否已经绑定到账号

  const stopWaitingTimer = useCallback(() => {
    if (waitingTimerRef.current) {
      clearInterval(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
  }, []);

  const startWaitingTimer = useCallback(() => {
    if (waitingTimerRef.current) return;
    setWaitingTime(0);
    waitingTimerRef.current = setInterval(() => {
      setWaitingTime((prev) => prev + 1);
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      stopWaitingTimer();
    };
  }, [stopWaitingTimer]);

  const fetchAudience = useCallback(async () => {
    const orderId = orderData?.id;
    if (!orderId) return;
    try {
      const { data } = await axiosInstance.get(`/order/${orderId}/audience`);
      const result = data?.data;
      setAudienceData(result);
      setTakeOrderAudienceView(result?.audienceView ?? null);
      if (result?.audienceView?.status !== 'processing') {
        if (audienceTimerRef.current) {
          clearInterval(audienceTimerRef.current);
          audienceTimerRef.current = null;
          stopWaitingTimer();
        }
      }
      // 当有数据，且受众模块报错时，清除报错状态
      if (
        result?.audienceView?.source === AudienceViewSource.FIRST_PARTY ||
        (result?.audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
          result?.audienceView?.status === SelfEstimatedStatus.SUCCESS)
      ) {
        setAudienceError(false);
        setHasAudienceData(true);
      }
    } catch (error) {
      console.error('audience polling error:', error);
      if (audienceTimerRef.current) {
        clearInterval(audienceTimerRef.current);
        audienceTimerRef.current = null;
      }
    }
  }, [orderData?.id, setTakeOrderAudienceView]);

  useEffect(() => {
    if (!showModalTakeOrder) return;
    // 详情只作兜底，不写回轮询结果；重新挂载时保留弹窗内的最新状态。
    const audienceView =
      useWebModalStore.getState().takeOrderAudienceView ??
      detailData?.audienceView;
    if (!audienceView) return;
    // 按最新结果清除错误，避免旧详情把未就绪状态改为可提交。
    if (
      audienceView.source === AudienceViewSource.FIRST_PARTY ||
      (audienceView.source === AudienceViewSource.SELF_ESTIMATED &&
        audienceView.status === SelfEstimatedStatus.SUCCESS)
    ) {
      setAudienceError(false);
      setHasAudienceData(true);
    }
  }, [showModalTakeOrder, detailData?.audienceView]);

  useEffect(() => {
    if (
      !showModalTakeOrder ||
      !orderData?.id ||
      orderData?.platform === PlatformItems.TWITTER ||
      orderData?.platform === PlatformItems.LINKEDIN
    )
      return;

    setAudienceData(null);
    fetchAudience();
    audienceTimerRef.current = setInterval(fetchAudience, 3000);

    return () => {
      if (audienceTimerRef.current) {
        clearInterval(audienceTimerRef.current);
        audienceTimerRef.current = null;
      }
    };
  }, [
    showModalTakeOrder,
    detailData?.audienceView,
    orderData?.id,
    fetchAudience
  ]);

  useEffect(() => {
    if (
      influencers?.some(
        (item: any) => item?.influencerId === orderInfluencerData?.influencerId
      )
    ) {
      setIsInfluencerBinding(true);
    } else {
      setIsInfluencerBinding(false);
    }
  }, [orderInfluencerData, influencers]);

  // 绑定平台
  const handleLinkChannel = useCallback(async () => {
    if (linkChannelLoading) return;
    setLinkChannelLoading(true);
    try {
      if (await showSocialBindingBrowserFallbackIfNeeded()) return;
      const platformType = orderData?.platform;
      if (!platformType) return;
      await fetchPlatformData();
      const { platformList, phylloSDKConfig } = usePlatformStore.getState();
      const workPlatform = platformList.find(
        (p: any) => p.platform === platformType
      );
      if (!workPlatform?.id) return;
      await openPhylloPlatformConnect({
        workPlatformId: workPlatform.id,
        platformType,
        phylloSDKConfig,
        onboarding: !getOrderId() && !influencers?.length,
        validateOrderId: false,
        urlShowModalAnalysis: false
      });
    } finally {
      setTimeout(() => {
        setLinkChannelLoading(false);
      }, 2000);
    }
  }, [orderData?.platform, fetchPlatformData, influencers]);

  const [styleType, setStyleType] = useState(0); // 样式类型 1: 加载中 2: 数据不足 3: 有数据  0: X & link的样式 & 默认样式
  const [btnStyleFlexCol, setBtnStyleFlexCol] = useState(true);

  // 判断 下面按钮怎么布局 移动端 & 非移动端
  useEffect(() => {
    if (isMobile()) {
      setBtnStyleFlexCol(true);
    } else {
      if (styleType === 3) {
        setBtnStyleFlexCol(false);
      } else {
        setBtnStyleFlexCol(true);
      }
    }
  }, [isMobile(), styleType]);

  // 判断使用什么样式
  useEffect(() => {
    const audienceView = audienceData?.audienceView || detailData?.audienceView;

    // 自研数据 处理中状态，是加载状态
    if (
      audienceView &&
      audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
      audienceView?.status === SelfEstimatedStatus.PROCESSING
    ) {
      startWaitingTimer();
      setStyleType(1);
    } else if (
      // 没有数据 且样本不足状态
      audienceView &&
      audienceView?.source === AudienceViewSource.NONE &&
      audienceView?.reason === NoneSource.INSUFFICIENT_AUDIENCE
    ) {
      setStyleType(2);
    } else if (
      // 有数据，一方数据 || 自研数据 成功状态
      audienceView &&
      (audienceView?.source === AudienceViewSource.FIRST_PARTY ||
        (audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
          audienceView?.status === SelfEstimatedStatus.SUCCESS))
    ) {
      setStyleType(3);
    }

    // 判断是否是ins订单
    const platformType = orderData?.platform;
    setIsInsOrder(platformType === PlatformItems.INSTAGRAM);
  }, [audienceData, orderData]);

  const waitedTime = (platformType: string) => {
    if (platformType === PlatformItems.TIKTOK) {
      return 2;
    } else if (platformType === PlatformItems.INSTAGRAM) {
      return 5;
    } else {
      return 3;
    }
  };

  return (
    <div className="flex flex-col gap-4 flex-1 pb-4">
      <div
        id="take-order-audience-demographics"
        className="text-[#0D0D0D] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug"
      >
        {t('modal.modal_take_order.audience_demographics.title', {
          defaultValue: 'Your audience demographics'
        })}{' '}
      </div>
      <div className="bg-[#EFF6FF] flex justify-start items-start gap-2 px-4 py-2 rounded-lg">
        <div className="mt-1">
          <SquareWarnIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 justify-start text-sm font-normal font-['DM_Sans'] leading-5">
          <span className="text-[#292524] ">
            {t('modal.modal_take_order.audience_demographics.tip', {
              defaultValue:
                'Sharing audience data helps brands better understand your audience and expedite the review process, ensuring that promoted content is accurately targeted. '
            })}
          </span>
          <span
            className="text-[#2563EB] cursor-pointer"
            onClick={() => setShowImg(true)}
          >
            {t('modal.modal_take_order.audience_demographics.tip_2', {
              defaultValue: "See how it appears on the brand's side"
            })}
          </span>
        </div>
      </div>
      <div
        className={`relative w-full min-h-[308px] shrink-0 overflow-hidden border rounded-lg ${audienceError ? 'border-red-500' : 'border-[#E5E5E5]'}`}
      >
        {styleType !== 3 ? (
          <img
            src="/upload-screenshots-bg.png"
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        ) : null}

        <div className="relative z-10 flex min-h-[308px] w-full flex-col items-center justify-center gap-6 py-2">
          {/* X & link的样式 */}
          {styleType === 0 && (
            <div className="text-text-color-primary text-base font-semibold font-['Plus_Jakarta_Sans'] leading-5">
              {t('modal.modal_take_order.audience_demographics.select_method', {
                defaultValue: 'Select method'
              })}
            </div>
          )}

          {/* 数据读取中 */}
          {styleType === 1 && (
            <div className="flex flex-col items-center justify-center gap-[6px]">
              <Loading width={48} height={48} color="#050505" />
              <div className="max-w-[380px] px-3 md:px-0 self-stretch text-center justify-start text-Colors-Text-text-primary-(900) text-base font-medium font-['Inter'] leading-6">
                {t(
                  'modal.modal_take_order.audience_demographics.reading_likes_comments',
                  {
                    defaultValue:
                      "Reading your channel's likes & comments to map your audience's location, gender and age."
                  }
                )}
              </div>
              <div className="max-w-[380px] self-stretch text-center justify-start text-Colors-Text-text-primary-(900) text-base font-medium font-['Inter'] leading-6">
                （{waitingTime}s / {waitedTime(orderData?.platform)}min）
              </div>
              <div className="text-center justify-start text-Colors-Text-text-placeholder text-xs font-normal font-['Inter'] leading-4">
                {t(
                  'modal.modal_take_order.audience_demographics.dont_want_to_wait',
                  {
                    defaultValue: "Don't want to wait? Share it directly"
                  }
                )}
              </div>
            </div>
          )}

          {/* 数据不足 */}
          {styleType === 2 && (
            <div className="flex flex-col items-center justify-center gap-[6px]">
              <div className="border border-[#E5E5E5] rounded-lg p-2">
                <TriangleExclamationIco2 className="w-5 h-5" />
              </div>
              <div className="self-stretch text-center justify-start text-Colors-Text-text-primary-(900) text-base font-medium font-['Inter'] leading-6">
                {t(
                  'modal.modal_take_order.audience_demographics.not_enough_public_engagement',
                  {
                    defaultValue: 'Not enough public engagement to analyze yet'
                  }
                )}
              </div>
              <div className="w-[350px] text-center justify-start text-Colors-Text-text-placeholder text-xs font-normal font-['Inter'] leading-4">
                {t(
                  'modal.modal_take_order.audience_demographics.not_enough_public_engagement_tip',
                  {
                    defaultValue:
                      "This account doesn't have enough likes and comments yet for a reliable audience profile. Upload your analytics or link your channel so we can analyze your audience directly."
                  }
                )}
              </div>
            </div>
          )}
          {/* 受众展示 */}
          {styleType === 3 && (
            <AudienceCharts
              audienceData={
                audienceData?.audienceView || detailData?.audienceView
              }
            />
          )}

          {styleType === 3 && (
            <div className="w-full px-4 justify-start text-[#737373] text-xs font-normal font-['Inter'] leading-4 mt-[-12px]">
              <div className="pt-4 border-t border-[#E5E5E5]">
                {t(
                  'modal.modal_take_order.audience_demographics.estimates_tip',
                  {
                    defaultValue:
                      "These are estimates. To show your exact numbers, link your channel or upload screenshots. It's optional."
                  }
                )}
              </div>
            </div>
          )}

          <div
            className={`w-full flex items-center justify-center ${!btnStyleFlexCol ? 'flex-row gap-3 px-4 pb-4' : 'flex-col'} ${isMobile() ? 'pb-4' : ''}`}
          >
            <Button
              className={`${!btnStyleFlexCol ? 'w-auto flex-1' : 'w-[260px]'}`}
              onClick={() => setShowModalUpdataAudience(true)}
            >
              <div className="flex items-center justify-center gap-2">
                <UploadIcon2 className="w-5 h-5 text-[#A3A3A3]" />
                <span>
                  {t(
                    'modal.modal_take_order.audience_demographics.upload_screenshots',
                    {
                      defaultValue: 'Upload screenshots'
                    }
                  )}
                </span>
              </div>
            </Button>
            {!isInsOrder && !isInfluencerBinding ? (
              <>
                <div>
                  {t('modal.modal_take_order.audience_demographics.or', {
                    defaultValue: 'or'
                  })}
                </div>
                <Button
                  className={`${!btnStyleFlexCol ? 'w-auto flex-1' : 'w-[260px]'}`}
                  onClick={handleLinkChannel}
                  loading={linkChannelLoading}
                >
                  <div className="flex items-center justify-center gap-2">
                    <NoMediaLinkIcon className="w-5 h-5 text-[#A3A3A3]" />
                    <span>
                      {t(
                        'modal.modal_take_order.audience_demographics.link_channel',
                        {
                          defaultValue: 'Link channel'
                        }
                      )}
                    </span>
                  </div>
                </Button>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      {audienceError && (
        <div className="text-red-500 text-sm font-medium font-['Inter'] leading-5">
          {t('modal.modal_take_order.audience_demographics.audience_error', {
            defaultValue:
              'Complete your audience demographics to submit your quote'
          })}
        </div>
      )}
      <ModalUpdataAudience
        showModalUpdataAudience={showModalUpdataAudience}
        setShowModalUpdataAudience={setShowModalUpdataAudience}
        uploadCallback={() => {
          if (orderData?.id) {
            fetchAudience();
            setThisLoading(true);
          }
        }}
      />
      {/* 图片展示 */}
      <ModalShowImg isOpen={showImg} setIsOpen={setShowImg} />
    </div>
  );
};

const AudienceCharts = ({ audienceData }: { audienceData: any }) => {
  const { t } = useTranslation();
  const [isFirstParty, setIsFirstParty] = useState(false);
  const countries: any[] = audienceData?.countries || [];
  const genders: any[] = audienceData?.genders || [];
  const ages: any[] = audienceData?.ages || [];

  const genderData = useMemo(
    () =>
      genders.map((g, i) => ({
        name: g.name === 'female' ? 'F' : g.name === 'male' ? 'M' : g.name,
        value: parseFloat(g.value) || 0,
        color: GENDERS_COLORS_[i % GENDERS_COLORS_.length]
      })),
    [genders]
  );

  const ageData = useMemo(
    () =>
      ages.map((a, i) => ({
        name: a.name,
        value: parseFloat(a.value) || 0,
        color: DONUT_COLORS[i % DONUT_COLORS.length]
      })),
    [ages]
  );

  const maxCountryValue = useMemo(
    () => Math.max(...countries.map((c) => parseFloat(c.value) || 0), 1),
    [countries]
  );

  useEffect(() => {
    setIsFirstParty(audienceData?.source === AudienceViewSource.FIRST_PARTY);
  }, [audienceData]);

  if (!countries.length && !genders.length && !ages.length) return null;

  return (
    <div className="w-full px-4 py-3">
      <div className="w-full flex items-center mb-3">
        <div className="text-[#0D0D0D] text-sm font-semibold border-r border-[#E5E5E5] pr-2 mr-2">
          {t('modal.modal_take_order.audience_demographics.details', {
            defaultValue: 'Details'
          })}
        </div>
        {!isFirstParty ? (
          <div className="flex items-center border border-[#E9D5FF] bg-[#FAF5FF] h-[22px] rounded-full px-2 w-fit">
            <AiReviewNoDataIcon className="w-3 h-3 text-[#7E22CE]" />
            <div className="ml-1 text-center text-[#7E22CE] text-xs font-medium font-['DM_Sans'] leading-4 gap-1 flex items-center">
              {t('modal.modal_take_order.audience_demographics.ai_estimate', {
                defaultValue: 'AI estimate'
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center border border-[#ABEFC6] bg-[#ECFDF3] h-[22px] rounded-full px-2 w-fit">
            <div className="text-center text-[#067647] text-xs font-medium font-['DM_Sans'] leading-4 gap-1 flex items-center">
              <BrandVerifiedIcon className="w-[14px] h-[14px] text-[#22C55E]" />
              {t('modal.modal_take_order.audience_demographics.verified_data', {
                defaultValue: 'Verified data'
              })}
            </div>
          </div>
        )}
      </div>
      <div
        className={`w-full bg-white rounded-xl flex gap-2 ${isMobile() ? 'flex-col' : 'flex-row'}`}
      >
        {/* Top locations */}
        {countries.length > 0 && (
          <div
            className={`flex-1 ${isMobile() ? 'max-w-full' : 'max-w-[280px]'} min-w-0 bg-[#FAFAFA] px-4 py-3 rounded-lg`}
          >
            <div className="text-[#0D0D0D] text-sm font-medium mb-3">
              {t('modal.modal_take_order.audience_demographics.top_locations', {
                defaultValue: 'Top 10 locations'
              })}
            </div>
            <div className="flex flex-col gap-[6px]">
              {countries.slice(0, 10).map((c: any) => {
                const pct = parseFloat(c.value) || 0;
                const barWidth = (pct / maxCountryValue) * 100;
                return (
                  <div key={c.code} className="flex items-center gap-2">
                    <span className="text-base leading-none w-6 text-center shrink-0">
                      {countryCodeToFlag(c.code)}
                    </span>
                    <span className="text-xs text-[#0D0D0D] font-medium w-8 shrink-0">
                      {c.code}
                    </span>
                    <div className="flex-1 h-3 bg-[#F5F5F5] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#FB6011]"
                        style={{
                          width: `${barWidth}%`,
                          transition: 'width 0.4s'
                        }}
                      />
                    </div>
                    <span className="text-xs text-[#0D0D0D] font-medium w-12 text-right shrink-0">
                      {pct.toFixed(pct >= 10 ? 0 : 2)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Gender + Age */}
        {(genders.length > 0 || ages.length > 0) && (
          <div className="min-w-[220px] flex flex-col gap-2 shrink-0">
            {genders.length > 0 && (
              <div className="bg-[#FAFAFA] px-4 py-3 rounded-lg">
                <div className="text-[#0D0D0D] text-sm font-medium mb-3">
                  {t('modal.modal_take_order.audience_demographics.gender', {
                    defaultValue: 'Gender'
                  })}
                </div>
                <div className="flex items-center gap-4">
                  <DonutChart data={genderData} size={80} boxSize={100} />
                  <div className="flex flex-col gap-1">
                    {genderData.map((g) => (
                      <div
                        key={g.name}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: g.color }}
                        />
                        <span className="text-[#0D0D0D] font-normal">
                          {g.value.toFixed(1)}% {g.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {ages.length > 0 && (
              <div className="min-w-[220px] bg-[#FAFAFA] px-4 py-3 rounded-lg">
                <div className="text-[#0D0D0D] text-sm font-medium mb-3">
                  {t('modal.modal_take_order.audience_demographics.age', {
                    defaultValue: 'Age'
                  })}
                </div>
                <div className="flex items-center gap-4">
                  <DonutChart data={ageData} size={80} boxSize={100} />
                  <div className="flex flex-col gap-1">
                    {ageData.map((a) => (
                      <div
                        key={a.name}
                        className="flex items-center gap-2 text-xs font-normal"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: a.color }}
                        />
                        <span className="text-[#0D0D0D] font-normal">
                          {a.name}{' '}
                          {t(
                            'modal.modal_take_order.audience_demographics.yrs',
                            {
                              defaultValue: 'yrs'
                            }
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ModalShowImg = ({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) => {
  const AudienceInsightsImage = () => {
    return (
      <div className="max-w-full">
        <img
          alt="audience-insights"
          src={'/audience-insights.png'}
          className="w-full h-auto object-contain"
        />
      </div>
    );
  };

  return (
    <WebModal
      visible={isOpen}
      canClose={true}
      handleClose={() => setIsOpen(false)}
      isShowFooter={false}
      isShowHeader={false}
      width={isMobile() ? 375 : undefined}
      className={`!z-[9999]
        ${isMobile() ? 'p-0' : 'p-0'}
        `}
    >
      <div className="absolute -top-12 right-2 rounded-full p-2 cursor-pointer hover:bg-gray-200">
        <CloseIcon className="w-6 h-6" onClick={() => setIsOpen(false)} />
      </div>
      <AudienceInsightsImage />
    </WebModal>
  );
};

export default AudienceDemographics;
